import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DashboardHeader from './DashboardHeader'
import { getDefaultRegion, isValidRegion } from '../../helpers/propertyTypeHelpers'
import { useAuth } from '../../contexts/AuthContext'
import Layout from '../../components/Layout'
import List from './List'
import MapPage from './Map'
import Analytics from './Analytics'
import Stats from './Stats'
import LoadingSkeleton from './LoadingSkeleton'
import { useProperties } from '../../hooks/useProperties'
import { useFilters } from '../../hooks/useFilters'
import { useAnalytics } from '../../hooks/useAnalytics'
// import Chat from './components/Chat'
const { useParams } = require('react-router-dom')

const Dashboard = () => {
  const navigate = useNavigate()
  const [view, setView] = useState('smallImages')
  const [hideNoPrice, setHideNoPrice] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const { authenticated, user } = useAuth()
  let { type, region, offerType } = useParams()

  if (!offerType) {
    offerType = 'prodej'
  }
  if (!type) {
    type = 'byt'
  }
  if (!region || !isValidRegion(type, region)) {
    region = getDefaultRegion(type, user)
  }

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setSearchTerm(searchInput.trim()), 300)
    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  const {
    districts,
    setDistricts,
    municipalities,
    setMunicipalities,
    layouts,
    selectedDisplayOptions,
    selectedSort,
    setSelectedSort,
    priceRange,
    dateRange,
    handleLayoutChange,
    handleDisplayOptionsChange,
    handlePriceRangeChange,
  } = useFilters()
  const {
    properties: originalData,
    allDistricts,
    pagination,
    counts,
    loading,
    loadingMore,
    loadMoreError,
    error,
    loadMore,
  } = useProperties({
    offerType,
    type,
    region,
    municipalities,
    districts,
    layouts,
    displayOptions: selectedDisplayOptions,
    sort: selectedSort,
    priceRange,
    search: searchTerm,
  })
  const shouldLoadAnalytics = offerType === 'prodej' && type === 'byt' && districts.length > 0
  const { analyticsData } = useAnalytics(region, shouldLoadAnalytics)
  const stats = { listingStatus: counts }
  const mapProps = {
    offerType,
    type,
    region,
    municipalities,
    districts,
    layouts,
    displayOptions: selectedDisplayOptions,
    priceRange,
    search: searchTerm,
  }

  // Don't show loading or error states if user is not authenticated
  // The AuthWrapper will handle showing the login form
  if (!authenticated) {
    return null
  }

  // Create header content for the Layout
  const headerContent = (
    <DashboardHeader
      user={user}
      districts={allDistricts}
      selectedMunicipalities={municipalities}
      onMunicipalitiesChange={setMunicipalities}
      selectedType={type}
      onTypeChange={(newType) => {
        let newRegion = region

        // If current region is not available for the new type, select the first available region
        if (!isValidRegion(newType, region)) {
          newRegion = getDefaultRegion(newType, user)
        }

        navigate(`/${offerType}/${newType}/${newRegion}`)
      }}
      selectedRegion={region}
      onRegionChange={(newRegion) => {
        navigate(`/${offerType}/${type}/${newRegion}`)
      }}
      selectedDistricts={districts}
      onDistrictsChange={setDistricts}
      selectedSort={selectedSort}
      onSortChange={setSelectedSort}
      view={view}
      onViewChange={(value) => setView(value)}
      originalData={originalData}
      hideNoPrice={hideNoPrice}
      onHideNoPriceChange={setHideNoPrice}
      selectedDisplayOptions={selectedDisplayOptions}
      onDisplayOptionsChange={handleDisplayOptionsChange}
      selectedOfferType={offerType}
      selectedDateRange={dateRange}
      onDateRangeChange={(range) => {
        // This will be handled by the filter hook
      }}
      minPrice={priceRange?.min}
      maxPrice={priceRange?.max}
      onPriceChange={handlePriceRangeChange}
      selectedLayouts={layouts}
      onLayoutChange={handleLayoutChange}
      searchInput={searchInput}
      onSearchInputChange={setSearchInput}
    />
  )

  if (loading || !originalData) {
    return (
      <Layout headerContent={headerContent}>
        {stats && <Stats stats={stats} />}
        {analyticsData && <Analytics city={region} analyticsData={analyticsData} selectedDistricts={districts} />}
        {view === 'smallImages' && <LoadingSkeleton smallImages={true} />}
        {view === 'largeImages' && <LoadingSkeleton smallImages={false} />}
        {view === 'map' && <MapPage {...mapProps} />}
        {view === 'table' && analyticsData && <Analytics city={region} analyticsData={analyticsData} show="full" />}
      </Layout>
    )
  }
  if (error) return <div>Error: {error.message}</div>

  return (
    <Layout headerContent={headerContent}>
      <Stats stats={stats} />
      {analyticsData && <Analytics city={region} analyticsData={analyticsData} selectedDistricts={districts} />}
      {view === 'smallImages' && (
        <List
          properties={originalData}
          smallImages={true}
          total={counts.total}
          hasMore={pagination.hasMore}
          loadingMore={loadingMore}
          loadMoreError={loadMoreError}
          onLoadMore={loadMore}
        />
      )}
      {view === 'largeImages' && (
        <List
          properties={originalData}
          smallImages={false}
          total={counts.total}
          hasMore={pagination.hasMore}
          loadingMore={loadingMore}
          loadMoreError={loadMoreError}
          onLoadMore={loadMore}
        />
      )}
      {view === 'map' && <MapPage {...mapProps} />}
      {view === 'table' && analyticsData && <Analytics city={region} analyticsData={analyticsData} show="full" />}

      {/* <Chat region={region} districts={districts} /> */}
    </Layout>
  )
}

export default Dashboard
